// @ts-nocheck
import { motion } from 'framer-motion';
import { YStack, XStack, Text, Paragraph } from 'tamagui';
import { Container } from './Container';
import { Eyebrow } from './Eyebrow';
import { useIsMobile } from '../hooks/useIsMobile';

const MotionDiv = motion.div;

/**
 * The product-page row: one band split into two cells by a vertical rule, text
 * on one side and a visual on the other, alternating down the page.
 *
 * Two details carry the look and are easy to lose:
 *
 *  - The heading sits at the top of its cell and the body copy at the bottom,
 *    not bunched together. The gap between them is what gives the row its
 *    architectural feel rather than looking like a centred marketing card.
 *  - The visual cell has no padding on its outer edge, so the artwork runs to
 *    the rule instead of floating inside a margin.
 *
 * `reverse` flips which side the text is on. Alternate it down the page — two
 * rows the same way and the rhythm disappears.
 *
 * With no `visual`, the row collapses to a single full-width column rather than
 * leaving an empty cell — which is the honest answer when there is no artwork,
 * and stops one being invented just to fill the space.
 *
 * `watermark` sets an oversized word behind the row, bled off the bottom-left and
 * clipped by the band. It sits underneath rather than replacing the visual: the
 * hero slot is where a reader learns what the product is, so evidence keeps it
 * and the type runs behind.
 */
export function SplitRow({ eyebrow, title, body, visual, watermark, reverse = false, tint = false, accent = '$amber9' }) {
  const isMobile = useIsMobile();

  const solo = !visual;

  const textCell = (
    <YStack
      flex={1}
      minWidth={isMobile ? '100%' : 320}
      paddingHorizontal="$5"
      paddingVertical={isMobile ? '$7' : '$10'}
      justifyContent={solo ? 'flex-start' : 'space-between'}
      gap={isMobile || solo ? '$5' : '$10'}
      borderRightWidth={!isMobile && !reverse && !solo ? 1 : 0}
      borderLeftWidth={!isMobile && reverse && !solo ? 1 : 0}
      borderColor="$color3"
    >
      <YStack gap="$4">
        {eyebrow && <Eyebrow accent={accent}>{eyebrow}</Eyebrow>}
        <Text
          fontSize={isMobile ? 30 : 42}
          fontWeight="700"
          letterSpacing={-1.5}
          lineHeight={isMobile ? 36 : 48}
          color="$color12"
          maxWidth={solo ? 720 : 460}
        >
          {title}
        </Text>
      </YStack>

      {body && (
        <Paragraph
          fontSize={isMobile ? '$4' : '$5'}
          color="$color8"
          lineHeight={isMobile ? 24 : 27}
          maxWidth={solo ? 760 : 480}
        >
          {body}
        </Paragraph>
      )}
    </YStack>
  );

  const visualCell = (
    <YStack
      flex={1}
      minWidth={isMobile ? '100%' : 320}
      paddingVertical={isMobile ? '$6' : '$9'}
      paddingHorizontal="$5"
      justifyContent="center"
      alignItems="center"
      overflow="hidden"
    >
      {visual}
    </YStack>
  );

  return (
    <YStack
      borderTopWidth={1}
      borderColor="$color3"
      backgroundColor={tint ? '$color2' : '$background'}
      position="relative"
      overflow="hidden"
    >
      {watermark && !isMobile && (
        <Text
          position="absolute"
          left={-18}
          bottom={-42}
          fontSize={190}
          fontWeight="700"
          letterSpacing={-8}
          lineHeight={190}
          color="$color3"
          pointerEvents="none"
          userSelect="none"
          aria-hidden="true"
        >
          {watermark}
        </Text>
      )}

      <Container paddingHorizontal={0} style={{ position: 'relative' }}>
        <MotionDiv
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-70px' }}
          transition={{ duration: 0.45 }}
        >
          <XStack flexDirection={isMobile ? 'column' : 'row'} alignItems="stretch">
            {solo ? (
              textCell
            ) : reverse && !isMobile ? (
              <>
                {visualCell}
                {textCell}
              </>
            ) : (
              <>
                {textCell}
                {visualCell}
              </>
            )}
          </XStack>
        </MotionDiv>
      </Container>
    </YStack>
  );
}
